import React, { ReactElement, useCallback, useMemo, useRef } from 'react';
import { useIdentityContext } from '@design-stack-vista/identity-provider';
import {
  FileTypeNotSupportedError,
  MaxFileSizeExceededError,
  UploadConfigurationProvider,
  UploadManagerProvider,
} from '@design-stack-vista/upload-components';
import { SubBrands, Tenants, VistaAsset } from '@design-stack-vista/vista-assets-sdk';
import { ValidationState } from '@vp/upload-and-print-validator';
import { sendEventTracking } from 'src/components/Analytics';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { UPLOAD_MAX_SIZE_KB, UPLOAD_SOURCE, UPLOAD_UNIFIED_EVENT, ValidationNames } from 'src/utils/Constants';
import { addPageAction, noticeError } from 'src/utils/newRelicUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { getValidationsForProduct } from 'src/utils/validationUtil';
import { usePromptContext } from './PromptContextProvider';

export const UploadProvider = ({ children }: React.PropsWithChildren): ReactElement => {
  const { auth: vpAuth } = useIdentityContext();
  const { setUpload, setIsUploading, setUploadRestrictionFailed, uploadMethod, fileType } = useUploadContext();
  const { productInformation, setProductInformation } = useProductContext();
  const { setValidations } = useValidationContext();
  const { trackingConfiguration } = useTrackingContext();
  const { setError } = useErrorContext();
  const {
    setShowProductOptionsPrompt,
    setShowSpecsAndTemplatePrompt,
    setShowValidationErrorPrompt,
  } = usePromptContext();

  const uploadStartTime = useRef<number>(0);

  const authProvider = useMemo(
    () => ({
      getAuthHeaders: async () => {
        return {
          Authorization: `Bearer ${vpAuth.getToken()}`,
        };
      },
    }),
    [vpAuth]
  );

  const pdfOnlyFileTypes: NonNullable<React.ComponentProps<
    typeof UploadConfigurationProvider
  >['supportedFileTypes']> = [
    {
      fileType: 'application/pdf',
      extensions: ['pdf'],
    },
  ];

  const fireUploadTracking = useCallback(() => {
    if (trackingConfiguration && productInformation.mpvId) {
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: UPLOAD_UNIFIED_EVENT,
        uploadSource: UPLOAD_SOURCE,
        uploadStep: 'upload_started',
        fileCount: 1,
        uploadMethod,
        fileType,
        label: `Upload`,
        category: 'Design - Upload and Print',
        variant: JSON.stringify(productInformation.selectedOptions),
      });
    }
  }, [trackingConfiguration, productInformation, uploadMethod, fileType]);

  const fireSuccessfulUploadTimeTracking = useCallback(() => {
    if (trackingConfiguration && productInformation.mpvId) {
      const uploadDuration = new Date().getTime() - uploadStartTime.current;
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: UPLOAD_UNIFIED_EVENT,
        uploadSource: UPLOAD_SOURCE,
        uploadStep: 'upload_completed',
        uploadMethod,
        fileType,
        fileCount: 1,
        label: 'Upload',
        category: 'Design - Upload and Print',
        variant: JSON.stringify(productInformation.selectedOptions),
        eventDetail: JSON.stringify({ uploadDuration }),
      });
    }
  }, [trackingConfiguration, productInformation, uploadStartTime, uploadMethod, fileType]);

  const fireFailedUploadTimeTracking = useCallback(
    (errorMessage: string) => {
      if (trackingConfiguration && productInformation.mpvId) {
        const uploadDuration = new Date().getTime() - uploadStartTime.current;
        sendEventTracking({
          ...trackingConfiguration,
          ...createTrackingDataForProduct(productInformation),
          action: UPLOAD_UNIFIED_EVENT,
          uploadSource: UPLOAD_SOURCE,
          uploadStep: 'upload_failed',
          uploadMethod,
          fileType,
          label: 'Upload',
          fileCount: 1,
          errorMessage,
          category: 'Design - Upload and Print',
          variant: JSON.stringify(productInformation.selectedOptions),
          eventDetail: JSON.stringify({ uploadDuration }),
        });
      }
    },
    [productInformation, trackingConfiguration, uploadStartTime, uploadMethod, fileType]
  );

  const onUpload = useCallback(() => {
    setUpload(null);
    setValidations(null);
    setIsUploading(true);
    setError(null);
    fireUploadTracking();
    uploadStartTime.current = new Date().getTime();
    setShowProductOptionsPrompt(false);
    setShowSpecsAndTemplatePrompt(false);
    setShowValidationErrorPrompt(false);
    setProductInformation({ ...productInformation, recommendedProductData: undefined });
  }, [
    fireUploadTracking,
    productInformation,
    setError,
    setIsUploading,
    setProductInformation,
    setShowProductOptionsPrompt,
    setShowSpecsAndTemplatePrompt,
    setShowValidationErrorPrompt,
    setUpload,
    setValidations,
  ]);

  const onUploadCompleted = useCallback(
    async (asset: VistaAsset): Promise<void> => {
      fireSuccessfulUploadTimeTracking();
      const uploadDuration = new Date().getTime() - uploadStartTime.current;

      addPageAction('unp-upload-success', {
        fileSize: asset.data?.info.storage?.fileSizeBytes,
        product: productInformation,
        durationMs: uploadDuration,
      });
    },
    [fireSuccessfulUploadTimeTracking, productInformation, uploadStartTime]
  );

  const onUploadError = useCallback(
    async (error: Error) => {
      fireFailedUploadTimeTracking(error.message);
      if (error instanceof FileTypeNotSupportedError || error instanceof MaxFileSizeExceededError) {
        setUploadRestrictionFailed(true);
        setIsUploading(false);
        setUpload(null);
        noticeError(error, {
          method: 'upload-error',
          error,
        });
        return;
      }

      const validations = await getValidationsForProduct(productInformation);
      const index = validations.findIndex(v => v.id === ValidationNames.CorruptValidator);
      validations[index].message = 'upload.corrupt file.message';
      validations[index].state = ValidationState.Error;
      setValidations(validations);
      setIsUploading(false);

      noticeError(error, {
        method: 'upload-error',
        error,
      });
    },
    [
      fireFailedUploadTimeTracking,
      setUploadRestrictionFailed,
      setIsUploading,
      setUpload,
      productInformation,
      setValidations,
    ]
  );

  return (
    <UploadConfigurationProvider supportedFileTypes={pdfOnlyFileTypes} maxFileSizeInKilobytes={UPLOAD_MAX_SIZE_KB}>
      <UploadManagerProvider
        authProvider={authProvider}
        subBrand={SubBrands.VistaPrint}
        writeTenant={Tenants.VistaPrint}
        experience="upload-and-print-client"
        onUploadCompleted={onUploadCompleted}
        onUpload={onUpload}
        onError={onUploadError}
        assetsExpireOn="P1D"
      >
        {children}
      </UploadManagerProvider>
    </UploadConfigurationProvider>
  );
};
